import React, { useState, useEffect } from "react";
import {
    Modal, ModalBody, ModalFooter, ModalHeader,
    Input, Row, Col
} from "reactstrap";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import {
    listSellersByParams, getSellerById, verifySeller,
    unverifySeller, activateSeller, deactivateSeller
} from "../../api/seller.api";
import StatusBadge from "../../components/StatusBadge";

const SellerManagement = () => {
    const [data, setData] = useState([]);
    const [query, setQuery] = useState("");
    const [modal, setModal] = useState(false);
    const [seller, setSeller] = useState({});
    const [pageNo, setPageNo] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [typeFilter, setTypeFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    // --- KEEPING EXISTING FUNCTIONS UNTOUCHED ---
    const fetchData = async () => {
        const skip = (pageNo - 1) * perPage;
        const res = await listSellersByParams({ skip, per_page: perPage, match: query, type: typeFilter, verification_status: statusFilter });
        if (res.data.data.length > 0) {
            const result = res.data.data[0];
            setData(result.data);
            setTotalRows(result.count);
        } else { setData([]); }
    };

    useEffect(() => { fetchData(); }, [query, pageNo, perPage, typeFilter, statusFilter]);

    const handleView = async (id) => {
        const res = await getSellerById(id);
        setSeller(res.data.data);
        setModal(true);
    };

    const handleVerify = async () => { await verifySeller(seller.id); toast.success("Seller Verified"); setModal(false); fetchData(); };
    const handleUnverify = async () => { await unverifySeller(seller.id); toast.success("Seller Unverified"); setModal(false); fetchData(); };
    const handleActivate = async () => { await activateSeller(seller.id); toast.success("Seller Activated"); setModal(false); fetchData(); };
    const handleDeactivate = async () => { await deactivateSeller(seller.id); toast.success("Seller Deactivated"); setModal(false); fetchData(); };

    // --- DESIGN STYLES ---
    const customStyles = {
        headRow: { style: { borderBottom: "1px solid #e5e7eb" } },
        headCells: { style: { color: "#6b7280", fontWeight: "600", fontSize: "13px", padding: "18px" } },
        cells: { style: { padding: "18px", fontSize: "14px", color: "#112347" } },
    };

    const columns = [
        { name: "Business Name", selector: r => r.business_name, grow: 1.5, style: { fontWeight: "700" } },
        { name: "Owner Name", selector: r => r.owner_name },
        { name: "Email address", selector: r => r.email, grow: 1.5 },
        { name: "Type", selector: r => r.type || "Trader" },
        { name: "GST", selector: r => r.gst_number || "---" },
        { name: "Status", center: true, cell: r => <StatusBadge status={r.verification_status} /> },
        { name: "Joined", selector: r => r.created_at ? r.created_at.split('T')[0] : "2024-01-15" },
        {
            name: "Actions",
            right: true,
            cell: (row) => (
                <button className="btn btn-link p-0 text-muted" onClick={() => handleView(row.id)} style={{ fontSize: "18px" }}>
                    🖊️
                </button>
            )
        }
    ];

    return (
        <div style={{ backgroundColor: "#ffffff", padding: "clamp(15px, 3vw, 30px)", borderRadius: "12px", border: "1px solid #e5e7eb" }}>

            {/* HEADER SECTION */}
            <div className="mb-4">
                <h4 className="fw-bold m-0" style={{ color: "#112347" }}>Seller Management</h4>
                <p className="text-muted small m-0">Verify and Moderate Platform User</p>
            </div>

            {/* SEARCH & FILTERS ROW */}
            <div className="d-flex flex-wrap gap-2 mb-4">
                <div style={{ position: "relative", flex: "1", minWidth: "200px", maxWidth: "400px" }}>
                    <span style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }}>🔍</span>
                    <Input
                        placeholder="Search User..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", paddingLeft: "40px", height: "45px" }}
                    />
                </div>
                <select
                    className="form-select w-auto"
                    value={typeFilter}
                    onChange={(e)=>{
                        setTypeFilter(e.target.value);
                        setPageNo(1);
                    }}
                    style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", minWidth: "140px" }}
                    >
                    <option value="">All Type</option>
                    <option value="manufacturer">Manufacturer</option>
                    <option value="reseller">Reseller</option>
                    <option value="hybrid">Hybrid</option>
                </select>

                <select
                className="form-select w-auto"
                value={statusFilter}
                onChange={(e)=>{
                    setStatusFilter(e.target.value);
                    setPageNo(1);
                }}
                style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", minWidth: "140px" }}
                >
                <option value="">All Status</option>
                <option value="VERIFIED">Verified</option>
                <option value="PENDING">Pending</option>
                </select>
            </div>

            {/* DATA TABLE */}
            <div className="table-responsive border rounded-3 overflow-hidden">
                <DataTable
                    columns={columns}
                    data={data}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    onChangePage={setPageNo}
                    onChangeRowsPerPage={setPerPage}
                    customStyles={customStyles}
                />
            </div>

            {/* SELLER DETAILS OVERLAY - FIXED & SCROLLABLE */}
            <Modal
                isOpen={modal}
                toggle={() => setModal(false)}
                centered
                style={{ maxWidth: '750px', width: '100%' }}
            >
                <ModalHeader className="border-0 pb-0 pt-4 px-4" toggle={() => setModal(false)}>
                    <div className="d-flex align-items-center gap-2">
                        <h4 className="fw-bold m-0" style={{ color: "#112347" }}>{seller.business_name}</h4>
                        <StatusBadge status={seller.verification_status} />
                    </div>
                    <p className="text-muted small m-0 mt-1">Complete seller registration details for review</p>
                </ModalHeader>

                {/* FIXED HEIGHT SCROLLABLE BODY */}
                <ModalBody className="px-4" style={{ height: '550px', overflowY: 'auto' }}>
                    <hr className="my-3 opacity-25" />

                    {/* BUSINESS INFORMATION */}
                    <div className="mb-4">
                        <h6 className="text-uppercase fw-bold text-muted mb-3" style={{ fontSize: '12px', letterSpacing: '1px' }}>Business Information</h6>
                        <Row className="g-4">
                            <Col xs="12" md="6">
                                <label className="text-muted small d-block mb-1">Business Name</label>
                                <span className="fw-bold text-dark" style={{ color: "#112347" }}>{seller.business_name}</span>
                            </Col>
                            <Col xs="12" md="6">
                                <label className="text-muted small d-block mb-1">Owner Name</label>
                                <span className="fw-bold text-dark">{seller.owner_name}</span>
                            </Col>
                            <Col xs="12" md="6">
                                <label className="text-muted small d-block mb-1">Seller Type</label>
                                <span className="fw-bold text-dark">🏭 {seller.type || "Manufacturer"}</span>
                            </Col>
                            <Col xs="12" md="6">
                                <label className="text-muted small d-block mb-1">GST Number</label>
                                <span className="fw-bold text-dark font-monospace">{seller.gst_number || "---"} <span className="text-success">✅</span></span>
                            </Col>
                        </Row>
                    </div>

                    <hr className="my-4 opacity-25" />

                    {/* CONTACT DETAILS */}
                    <div className="mb-4">
                        <h6 className="text-uppercase fw-bold text-muted mb-3" style={{ fontSize: '12px', letterSpacing: '1px' }}>Contact Details</h6>
                        <Row className="g-3">
                            <Col xs="12" md="6" className="d-flex align-items-center gap-2">
                                <span className="fs-5">✉️</span> <span className="fw-bold text-dark">{seller.email}</span>
                            </Col>
                            <Col xs="12" md="6" className="d-flex align-items-center gap-2">
                                <span className="fs-5">📞</span> <span className="fw-bold text-dark">{seller.contact_number}</span>
                            </Col>
                        </Row>
                    </div>

                    <hr className="my-4 opacity-25" />

                    {/* LOCATION */}
                    <div className="mb-4">
                        <h6 className="text-uppercase fw-bold text-muted mb-3" style={{ fontSize: '12px', letterSpacing: '1px' }}>Location</h6>
                        <div className="d-flex align-items-center gap-2 text-dark fw-bold">
                            <span className="fs-5">📍</span> {seller.city || "Mumbai"}, {seller.state || "Maharashtra"}
                        </div>
                    </div>

                    <hr className="my-4 opacity-25" />

                    {/* DESCRIPTION */}
                    <div className="mb-4">
                        <h6 className="text-uppercase fw-bold text-muted mb-3" style={{ fontSize: '12px', letterSpacing: '1px' }}>Business Description</h6>
                        <p className="text-muted" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                            {seller.business_description || "No description provided for this business."}
                        </p>
                    </div>

                    <hr className="my-4 opacity-25" />

                    {/* DOCUMENTS */}
                    <div className="mb-3">
                        <h6 className="text-uppercase fw-bold text-muted mb-3" style={{ fontSize: '12px', letterSpacing: '1px' }}>
                            Verification Documents
                        </h6>

                        {seller.documents ? (
                            <div className="p-3 border rounded d-flex justify-content-between align-items-center bg-light flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-3">
                                <span className="fs-3">📄</span>
                                <span className="fw-bold" style={{ color: "#112347" }}>
                                {seller.documents}
                                </span>
                            </div>

                            <a
                                href={`https://aaeternitas.com/uploads/documents/${seller.documents}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-link text-dark fw-bold text-decoration-none"
                            >
                                👁️ View
                            </a>
                            </div>
                        ) : (
                            <div className="text-muted small">No document uploaded</div>
                        )}
                        </div>
                </ModalBody>

                {/* FIXED FOOTER */}
                <ModalFooter className="border-0 px-4 pb-4 pt-3 d-flex justify-content-between align-items-center bg-white flex-wrap gap-3">
                    <div className="text-muted small d-flex align-items-center gap-2">
                        <span className="fs-5">📅</span> Registration Date: {seller.created_at ? seller.created_at.split('T')[0] : "2024-02-01"}
                    </div>
                    <div className="d-flex gap-2">

                        {/* ❌ SHOW ONLY IF PENDING */}
                        {["PENDING", "UNVERIFIED"].includes(seller.verification_status) && (
                        <button
                            className="btn btn-success fw-bold px-4"
                            style={{ backgroundColor: "#10b981", border: "none", borderRadius: "8px" }}
                            onClick={handleVerify}
                        >
                            ✓ Approve & Verify
                        </button>
                        )}

                        {/* ❌ SHOW ONLY IF VERIFIED */}
                        {seller.verification_status === "VERIFIED" && (
                            <button
                            className="btn btn-outline-danger fw-bold px-4"
                            style={{ borderRadius: "8px", border: "1px solid #fee2e2" }}
                            onClick={handleUnverify}
                            >
                            ✖ Reject Registration
                            </button>
                        )}

                    </div>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default SellerManagement;